import { CommonModule, DOCUMENT, NgClass, isPlatformBrowser } from '@angular/common';
import { Component, ChangeDetectorRef, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-banner-project',
  templateUrl: './banner-project.component.html',
  styleUrl: './banner-project.component.scss',
  standalone: true,
  imports: [
    NgClass,
    CommonModule
  ]
})
export class BannerProjectComponent implements OnInit, OnDestroy {
  private readonly siteUrl = environment.SITE_URL;

  constructor(
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object,
    private titleService: Title,
    private metaService: Meta,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  images = [
    {
      url: 'assets/images/projects/project-section/3c84b2dc-5d27-49e2-a559-b0c9318c52c6.webp',
      caption: 'Imagen 1',
      isExpanded: true
    },
    {
      url: 'assets/images/projects/project-section/la salle bajio.webp',
      caption: 'Imagen 2',
      isExpanded: false
    },
    {
      url: 'assets/images/projects/project-section/ine3.webp',
      caption: 'Imagen 3',
      isExpanded: false
    },
    {
      url: 'assets/images/projects/project-section/Torre Delta 4.webp',
      caption: 'Imagen 1',
      isExpanded: true
    },
    {
      url: 'assets/images/projects/project-section/purina.webp',
      caption: 'Imagen 2',
      isExpanded: false
    },
    {
      url: 'assets/images/projects/project-section/salle-bajio.webp',
      caption: 'Imagen 3',
      isExpanded: false
    }
  ];


  // Desktop layout
  counter = 0;
  numberRows = Math.ceil(this.images.length / 3);
  intervalId: any;
  textIntervalId: any;
  expandedIndex = 0;
  currentBlockIndex = 0;
  visibleImages: any[] = [];

  // Subtitle text rotation
  subtitles = [
    'Descubre nuestras participaciones más destacadas',
    'Innovación y excelencia en cada proyecto',
    'Construyendo el futuro juntos',
    'Experiencia que transforma ideas en realidad'
  ];
  currentSubtitleIndex = 0;
  currentSubtitle = this.subtitles[0];

  // Layout control
  currentLayout: 'mobile' | 'tablet' | 'desktop' = 'desktop';

  // Mobile layout
  mobileIntervalId: any;
  mobileCurrentIndex = 0;
  mobileStackImages: any[] = [];
  isAnimating = false;

  // Tablet layout
  tabletIntervalId: any;
  tabletCurrentIndex = 0;
  tabletImages: any[] = [];

  // Resize listener
  resizeListener: any;
  resizeTimeout: any;
  private aosInitialized = false;

  ngOnInit() {
    this.setMetaTags();
    if (isPlatformBrowser(this.platformId)) {
      if (!this.aosInitialized) {
        import('aos').then((mod: any) => {
          const AOS = mod?.default ?? mod;
          AOS.init({
            duration: 1000,
            once: true,
            mirror: false,
            offset: 100,
            easing: 'ease-out-cubic',
            disable: false
          });
          AOS.refresh();
          this.aosInitialized = true;
        });
      }

      this.detectLayout();
      this.initializeLayout();

      // Resize listener layout detection
      this.resizeListener = () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          const previousLayout = this.currentLayout;
          this.detectLayout();

          if (previousLayout !== this.currentLayout) {
            this.initializeLayout();
          }
        }, 150);
      };
      window.addEventListener('resize', this.resizeListener);

      // Text rotation interval
      this.textIntervalId = setInterval(() => {
        const subtitle = document.querySelector('.section-subtitle');
        if (subtitle) {
          subtitle.classList.add('fade-out');

          setTimeout(() => {
            this.currentSubtitleIndex = (this.currentSubtitleIndex + 1) % this.subtitles.length;
            this.currentSubtitle = this.subtitles[this.currentSubtitleIndex];
            this.cdr.detectChanges();

            subtitle.classList.remove('fade-out');
          }, 300);
        }
      }, 3000);
    }
  }

  detectLayout() {
    if (!isPlatformBrowser(this.platformId)) return;

    const width = window.innerWidth;
    let newLayout: 'mobile' | 'tablet' | 'desktop';

    if (width <= 768) {
      newLayout = 'mobile';
    } else if (width > 768 && width <= 1200) {
      newLayout = 'tablet';
    } else {
      newLayout = 'desktop';
    }

    if (this.currentLayout !== newLayout) {
      const previousLayout = this.currentLayout;
      this.currentLayout = newLayout;
      this.stopLayoutIntervals(previousLayout);
    }
  }

  stopLayoutIntervals(layout: 'mobile' | 'tablet' | 'desktop') {
    switch (layout) {
      case 'mobile':
        if (this.mobileIntervalId) {
          clearInterval(this.mobileIntervalId);
          this.mobileIntervalId = null;
        }
        break;
      case 'tablet':
        if (this.tabletIntervalId) {
          clearInterval(this.tabletIntervalId);
          this.tabletIntervalId = null;
        }
        break;
      case 'desktop':
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
        break;
    }
  }

  private setMetaTags(): void {
    const metaTitle = 'Galería de proyectos | Paredes y Asociados';
    const metaDescription = 'Explora imágenes destacadas de proyectos de construcción realizados por Paredes y Asociados en México.';
    const url = `${this.siteUrl}/banner/`;
    const image = this.toAbsoluteUrl('/assets/images/projects/expansion-emerson-estado-de-mexico/126-expansion-emerson-estado-de-mexico.webp');

    this.titleService.setTitle(metaTitle);
    this.metaService.updateTag({ name: 'description', content: metaDescription });

    this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
    this.metaService.updateTag({ name: 'description', content: metaDescription });
    this.setCanonicalUrl(url);

    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({ property: 'og:locale', content: 'es_MX' });
    this.metaService.updateTag({ property: 'og:site_name', content: 'Paredes y Asociados' });

    this.metaService.updateTag({ property: 'og:title', content: metaTitle });
    this.metaService.updateTag({ property: 'og:description', content: metaDescription });
    this.metaService.updateTag({ property: 'og:url', content: url });
    this.metaService.updateTag({ property: 'og:image', content: image });
    this.metaService.updateTag({ property: 'og:image:alt', content: 'Paredes y Asociados' });

    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: metaTitle });
    this.metaService.updateTag({ name: 'twitter:description', content: metaDescription });
    this.metaService.updateTag({ name: 'twitter:url', content: url });
    this.metaService.updateTag({ name: 'twitter:image', content: image });
  }

  private setCanonicalUrl(url: string): void {
    const head = this.document.head;
    let link = this.document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private toAbsoluteUrl(path: string): string {
    if (!path) {
      return `${this.siteUrl}/assets/images/logos/logo-full.webp`;
    }
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return `${this.siteUrl}${path}`;
  }

  initializeLayout() {
    switch (this.currentLayout) {
      case 'mobile':
        this.initMobileLayout();
        break;
      case 'tablet':
        this.initTabletLayout();
        break;
      case 'desktop':
        this.initDesktopLayout();
        break;
    }

    this.cdr.detectChanges();
  }

  stopAllIntervals() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.mobileIntervalId) {
      clearInterval(this.mobileIntervalId);
      this.mobileIntervalId = null;
    }
    if (this.tabletIntervalId) {
      clearInterval(this.tabletIntervalId);
      this.tabletIntervalId = null;
    }
  }

  initMobileLayout() {
    if (this.mobileIntervalId) return;

    const totalCards = 3;
    let currentPositions = ['top', 'right', 'left'];

    // Set initial positions on the next tick
    setTimeout(() => {
      const cards = document.querySelectorAll('.mobile-layout-2 .card');
      cards.forEach((card, index) => {
        (card as HTMLElement).setAttribute('data-position', currentPositions[index]);
      });
    }, 0);

    this.mobileIntervalId = setInterval(() => {
      this.isAnimating = true;
      const cards = document.querySelectorAll('.mobile-layout-2 .card');

      // Rotate positions
      currentPositions = [
        currentPositions[2], // left → top
        currentPositions[0], // top → right
        currentPositions[1]  // right → left
      ];

      requestAnimationFrame(() => {
        cards.forEach((card, index) => {
          (card as HTMLElement).setAttribute('data-position', currentPositions[index]);
        });
      });

      setTimeout(() => this.isAnimating = false, 2000);
    }, 3500);
  }

  initTabletLayout() {
    if (this.tabletIntervalId) return;

    this.tabletCurrentIndex = 0;
    this.updateTabletImages();

    this.tabletIntervalId = setInterval(() => {
      this.tabletCurrentIndex = (this.tabletCurrentIndex + 2) % this.images.length;
      this.updateTabletImages();
      this.cdr.detectChanges();
    }, 5000);
  }

  updateTabletImages() {
    const firstIndex = this.tabletCurrentIndex;
    const secondIndex = (this.tabletCurrentIndex + 1) % this.images.length;

    this.tabletImages = [
      { ...this.images[firstIndex], isLarge: true },
      { ...this.images[secondIndex], isLarge: false }
    ];
  }

  initDesktopLayout() {
    if (this.intervalId) return;

    this.currentBlockIndex = 0;
    this.expandedIndex = 0;
    this.updateVisibleImages();

    // Interval of 2 seconds for sequential expansion
    this.intervalId = setInterval(() => {
      const totalBlocks = Math.ceil(this.images.length / 3);

      this.expandedIndex++;

      if (this.expandedIndex >= 3) {
        this.currentBlockIndex = (this.currentBlockIndex + 1) % totalBlocks;
        this.updateVisibleImages();
        this.expandedIndex = 0;
      }

      this.cdr.detectChanges();
    }, 2000);
  }

  updateVisibleImages() {
    const startIndex = this.currentBlockIndex * 3;
    const endIndex = Math.min(startIndex + 3, this.images.length);
    this.visibleImages = this.images.slice(startIndex, endIndex).map((img, index) => ({
      ...img,
      isExpanded: index === this.expandedIndex
    }));
  }

  ngOnDestroy() {
    this.stopAllIntervals();

    if (this.textIntervalId) {
      clearInterval(this.textIntervalId);
      this.textIntervalId = null;
    }

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    if (this.resizeListener && isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  getBackgroundImage(index: number): string {
    if (index >= 0 && index < this.images.length) {
      return `url(${this.images[index].url})`;
    }
    return '';
  }

  trackByFn(index: number, item: any): number | string {
    return item.id || item.url || index;
  }
}
